import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MESSAGE_FROM,
  MESSAGE_TYPES,
  NUDGYT_AGENT_MESSAGE_TYPES,
  VAD_STATES,
  NUDGYT_AGENT_STOPPABLE_RESPONSE_TYPES,
  STOP_RESPONSE_GATE_FALLBACK_MS
} from '../constants/simulationConstants';
import { isIOS, postMessageUserText, postMessage } from '../helpers/utils';
import { buildUserEmotionWsField } from '../helpers/userEmotion';
import { buildAudioQueryPayload } from '../helpers/liveChatAudio';
import { useSimulationContext } from '../providers/SimulationProvider';
import useAnamNudgytAgentAudioQueue from './useAnamNudgytAgentrAudioQueue';
import useAudioQueue from './useAudioQueue';
import useCoreLiveChat from './useCoreLiveChat';
import useRequestLiveChatUserEmotion from './useRequestLiveChatUserEmotion';
import useResponseTimeout from './useResponseTimeout';
import useCoreLiveChatListener from './useCoreLiveChatListener';
import useCoreRequestedUserEmotionListener from './useCoreRequestedUserEmotionListener';
import useCoreRequestUserEmotionInterval from './useCoreRequestUserEmotionInterval';
import nudgytAgentConfig from '../config/nudgytAgentConfig';
import createNudgytAgentChatSocket from '../websocket/nudgytAgentChatSocket';

/**
 * Nudgyt Agent client for Basic + Nudgyt Core on an ANAM avatar.
 * Coaching uses {@link useAnamNudgytAgentClient} instead.
 */
function useAnamNudgytAgentCoreClient({
  aiResponsePlaybackActiveRef,
  isVideoStreamReady,
  handleSendAudioChunk,
  handleEndAudioSequence
} = {}) {
  const isLocalAudioRouteRef = useRef(false);
  const shouldRouteLocalRef = useRef(false);

  const [isWsConnected, setIsWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const isIntentionalCloseRef = useRef(false);
  const aiResponseInFlightRef = useRef(false);
  const [isAiResponseInFlight, setIsAiResponseInFlightState] = useState(false);
  const setAiResponseInFlight = useCallback((inFlight) => {
    aiResponseInFlightRef.current = inFlight;
    setIsAiResponseInFlightState(inFlight);
  }, []);
  const startResponseTimeoutRef = useRef(null);
  const clearResponseTimeoutRef = useRef(null);
  const lastAudioQueryUserEmotionRef = useRef(null);
  const lastTextQueryUserEmotionRef = useRef(null);
  /**
   * Set while a stop is pending the server's `cancelled` echo. Content chunks
   * of the stopped reply are dropped rather than played or forwarded — the
   * in-flight flag alone cannot do this, because the learner's next message
   * sets it back to true and lets stragglers through behind it.
   */
  const stoppedResponseRef = useRef(false);
  const stopResponseFallbackTimeoutRef = useRef(null);

  const requestLiveChatUserEmotion = useRequestLiveChatUserEmotion();

  const { enqueueChunk, enqueueEndSequence, flushQueue, initAudio } =
    useAnamNudgytAgentAudioQueue({
      handleSendAudioChunk,
      handleEndAudioSequence
    });

  const { enqueueAudio, clearAudioQueue } = useAudioQueue();

  const {
    userText,
    documentIds,
    textQueryUserEmotion,
    userEmotion,
    userInteract,
    isAiTalking,
    isAvatarHidden,
    nudgytSessionToken,
    isClientInitialized,
    isEmotionRecognitionEnabled,
    isLiveChatActive,
    isStopResponseRequested,
    setUserText,
    setIsClientInitialized,
    setDocumentIds,
    setIsStopResponseRequested,
    setIsUserInterrupt,
    setTextQueryUserEmotion,
    setUserInteract
  } = useSimulationContext();

  const hasSession = Boolean(nudgytSessionToken);

  const isAiTalkingGuardRef = useRef(isAiTalking);
  isAiTalkingGuardRef.current = isAiTalking;

  shouldRouteLocalRef.current = isAvatarHidden && !isVideoStreamReady;

  const resetClientState = useCallback(() => {
    setUserText('');
    setDocumentIds([]);
  }, [setDocumentIds, setUserText]);

  // sendAudioQuery is created once; emotion is requested from the parent at send
  // time (USER_EMOTION_LIVE_CHAT), matching Convai's end-of-turn refresh.
  const sendAudioQuery = useCallback(
    async (base64Audio, format) => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) return false;

      const userEmotion = await requestLiveChatUserEmotion();
      lastAudioQueryUserEmotionRef.current = userEmotion;

      wsRef.current.send(
        JSON.stringify(
          buildAudioQueryPayload({
            base64Audio,
            format,
            userEmotion
          })
        )
      );
      startResponseTimeoutRef.current?.();
      return true;
    },
    [requestLiveChatUserEmotion]
  );

  // Above useResponseTimeout, whose onTimeout needs these; the timeout
  // functions are reached through the refs assigned below.
  const { postLiveChatStatus, startListening } = useCoreLiveChat({
    sendAudioQuery,
    // Anam plays replies through its own stream, so barge-in must also reach
    // the persona (AnamVideoStream.jsx -> interruptPersona).
    onUserBargeIn: () => {
      flushQueue();
      clearAudioQueue();
      setIsUserInterrupt(true);
    },
    clearResponseTimeout: () => clearResponseTimeoutRef.current?.()
  });

  /**
   * Reopens the gate closed by a stop request, so the next reply streams
   * normally.
   */
  const clearStoppedResponseGate = useCallback(() => {
    stoppedResponseRef.current = false;
    if (stopResponseFallbackTimeoutRef.current) {
      clearTimeout(stopResponseFallbackTimeoutRef.current);
      stopResponseFallbackTimeoutRef.current = null;
    }
  }, []);

  const { startResponseTimeout, clearResponseTimeout } = useResponseTimeout({
    onTimeout: () => {
      resetClientState();
      setAiResponseInFlight(false);
      postLiveChatStatus(VAD_STATES.IDLE);
      startListening();
    }
  });

  useEffect(() => {
    startResponseTimeoutRef.current = startResponseTimeout;
    clearResponseTimeoutRef.current = clearResponseTimeout;
  }, [clearResponseTimeout, startResponseTimeout]);

  useCoreLiveChatListener();
  useCoreRequestedUserEmotionListener(isEmotionRecognitionEnabled);
  useCoreRequestUserEmotionInterval({
    isClientInitialized,
    isAiTalking,
    isAiResponseInFlight
  });

  const handleAgentMessage = useCallback(
    (aiMessage) => {
      switch (aiMessage.type) {
        case NUDGYT_AGENT_MESSAGE_TYPES.TEXT_QUERY:
          startResponseTimeout();
          postMessageUserText({
            text: aiMessage.text,
            userEmotion:
              textQueryUserEmotion ??
              lastTextQueryUserEmotionRef.current ??
              lastAudioQueryUserEmotionRef.current
          });
          lastTextQueryUserEmotionRef.current = null;
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_START:
          if (!isLocalAudioRouteRef.current) clearAudioQueue();
          resetClientState();
          clearResponseTimeout();
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_ENDED:
          enqueueEndSequence();
          isLocalAudioRouteRef.current = shouldRouteLocalRef.current;
          // Fallback unmute: the Anam analyser is frozen while the avatar is
          // hidden, so isAiTalking may never cycle.
          startListening({ onlyUnmute: true });
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.ERROR:
        case NUDGYT_AGENT_MESSAGE_TYPES.CANCELLED:
          handleEndAudioSequence?.();
          isLocalAudioRouteRef.current = shouldRouteLocalRef.current;
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.AUDIO_RESPONSE:
          if (isLocalAudioRouteRef.current) {
            enqueueAudio({
              audio: aiMessage.audio,
              viseme: aiMessage.viseme || []
            });
            break;
          }
          if (aiResponsePlaybackActiveRef?.current !== undefined) {
            aiResponsePlaybackActiveRef.current = true;
          }
          enqueueChunk(aiMessage.audio);
          break;
        default:
      }
    },
    [
      aiResponsePlaybackActiveRef,
      clearAudioQueue,
      clearResponseTimeout,
      enqueueAudio,
      enqueueChunk,
      enqueueEndSequence,
      handleEndAudioSequence,
      resetClientState,
      startListening,
      startResponseTimeout,
      textQueryUserEmotion
    ]
  );

  const onMessageRef = useRef(handleAgentMessage);
  onMessageRef.current = handleAgentMessage;

  const closeLiveSocket = (reason = 'Closing connection') => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const socket = wsRef.current;
    if (!socket) return;

    isIntentionalCloseRef.current = true;
    if (
      socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING
    ) {
      socket.close(1000, reason);
    }
    wsRef.current = null;
    setIsWsConnected(false);
  };

  const scheduleReconnect = () => {
    reconnectAttemptsRef.current += 1;
    reconnectTimeoutRef.current = setTimeout(() => {
      connectWebSocket();
    }, nudgytAgentConfig.reconnectDelay);
  };

  const connectWebSocket = () => {
    if (!hasSession) {
      console.error(
        '[Nudgyt Agent Core Client] Missing auth: nudgytSessionToken'
      );
      return;
    }

    closeLiveSocket('Replacing connection');

    const ws = createNudgytAgentChatSocket({
      base: nudgytAgentConfig.baseUrl,
      sessionToken: nudgytSessionToken
    });

    if (!ws) return;

    ws.onopen = () => {
      if (wsRef.current !== ws) {
        isIntentionalCloseRef.current = true;
        ws.close(1000, 'Superseded by newer connection');
        isIntentionalCloseRef.current = false;
        return;
      }

      setIsWsConnected(true);
      reconnectAttemptsRef.current = 0;
      isIntentionalCloseRef.current = false;
      initAudio();
    };

    ws.onmessage = (ev) => {
      let aiMessage;
      try {
        aiMessage = JSON.parse(ev.data);
      } catch {
        return;
      }

      const isGatedTextToken =
        !aiResponseInFlightRef.current &&
        aiMessage.type === NUDGYT_AGENT_MESSAGE_TYPES.TEXT_TOKEN;

      if (isGatedTextToken) {
        return;
      }

      // Chunks still arriving from a reply the learner stopped. Returning here
      // is before both the handler below and the postMessage forward, so
      // nothing is played or reaches the parent. Ordered delivery means every
      // straggler precedes the `cancelled` echo that reopens the gate.
      if (
        stoppedResponseRef.current &&
        NUDGYT_AGENT_STOPPABLE_RESPONSE_TYPES.includes(aiMessage.type)
      ) {
        return;
      }

      switch (aiMessage.type) {
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_START:
          setAiResponseInFlight(true);
          // A reply that starts after a stop is a new one, and every straggler
          // from the stopped reply is already behind us in the stream. This is
          // what reopens the gate when the agent skips the `cancelled` echo.
          clearStoppedResponseGate();
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.EMOTION:
          setAiResponseInFlight(true);
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_ENDED:
          setAiResponseInFlight(false);
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.ERROR:
        case NUDGYT_AGENT_MESSAGE_TYPES.CANCELLED:
          setAiResponseInFlight(false);
          clearStoppedResponseGate();
          break;
        default:
      }

      onMessageRef.current?.(aiMessage);

      if (aiMessage.type !== NUDGYT_AGENT_MESSAGE_TYPES.TEXT_QUERY) {
        postMessage({
          type: MESSAGE_TYPES.CHAT,
          payload: { from: MESSAGE_FROM.AI, ...aiMessage }
        });
      }
    };

    ws.onerror = (error) => {
      console.error('[Nudgyt Agent Core Client] WebSocket error:', error);
      clearResponseTimeout();
    };

    ws.onclose = (event) => {
      if (event.target !== wsRef.current) {
        isIntentionalCloseRef.current = false;
        return;
      }

      setIsWsConnected(false);
      wsRef.current = null;

      if (
        !isIntentionalCloseRef.current &&
        shouldReconnectRef.current &&
        reconnectAttemptsRef.current < nudgytAgentConfig.maxReconnectAttempts
      ) {
        scheduleReconnect();
      } else if (
        reconnectAttemptsRef.current >= nudgytAgentConfig.maxReconnectAttempts
      ) {
        postMessage({
          type: MESSAGE_TYPES.CHAT,
          error: 'Connection failed'
        });
      }

      isIntentionalCloseRef.current = false;
    };

    wsRef.current = ws;
  };

  useEffect(() => {
    if (!hasSession) {
      closeLiveSocket('Missing auth');
      return undefined;
    }

    shouldReconnectRef.current = true;
    reconnectAttemptsRef.current = 0;
    connectWebSocket();

    return () => {
      shouldReconnectRef.current = false;
      closeLiveSocket('Component unmounting');
      setAiResponseInFlight(false);
      clearStoppedResponseGate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nudgytSessionToken]);

  useEffect(() => {
    if (!hasSession) return;

    const onParentMessage = (event) => {
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (
        data?.type === MESSAGE_TYPES.CHAT &&
        data?.payload?.type === NUDGYT_AGENT_MESSAGE_TYPES.STOP_AUDIO
      ) {
        resetClientState();
      }
    };

    window.addEventListener('message', onParentMessage);
    return () => window.removeEventListener('message', onParentMessage);
  }, [hasSession, resetClientState]);

  useEffect(() => {
    if (!hasSession || !isStopResponseRequested) return;

    clearResponseTimeout();
    resetClientState();
    flushQueue();
    clearAudioQueue();
    setIsUserInterrupt(true);
    setAiResponseInFlight(false);
    if (aiResponsePlaybackActiveRef?.current !== undefined) {
      aiResponsePlaybackActiveRef.current = false;
    }
    // Stop ends the reply as far as routing is concerned, exactly as
    // response_ended and cancelled do. Waiting for the echo instead would keep
    // the coach on local audio after the learner has revealed the avatar.
    isLocalAudioRouteRef.current = shouldRouteLocalRef.current;

    stoppedResponseRef.current = true;
    // Safety valve: an agent that never echoes `cancelled` would otherwise
    // leave the gate shut and mute every later reply.
    if (stopResponseFallbackTimeoutRef.current)
      clearTimeout(stopResponseFallbackTimeoutRef.current);
    stopResponseFallbackTimeoutRef.current = setTimeout(() => {
      stopResponseFallbackTimeoutRef.current = null;
      stoppedResponseRef.current = false;
      isLocalAudioRouteRef.current = shouldRouteLocalRef.current;
    }, STOP_RESPONSE_GATE_FALLBACK_MS);

    setIsStopResponseRequested(false);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ type: NUDGYT_AGENT_MESSAGE_TYPES.STOP_RESPONSE })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStopResponseRequested, hasSession]);

  useEffect(() => {
    if (!hasSession) return;
    if (!isWsConnected || !wsRef.current) return;
    if (!userText?.trim() && !(documentIds?.length > 0)) return;

    setAiResponseInFlight(true);

    const userEmotionField = buildUserEmotionWsField(textQueryUserEmotion);

    const textQueryPayload = {
      type: NUDGYT_AGENT_MESSAGE_TYPES.TEXT_QUERY,
      ...(userText?.trim() && { text: userText }),
      ...(documentIds?.length > 0 && { document_ids: documentIds }),
      local_time: new Date().toISOString().slice(0, 19).replace('T', ' '),
      mute_audio: false,
      ...(userEmotionField ?? {})
    };

    lastTextQueryUserEmotionRef.current = textQueryUserEmotion;

    wsRef.current.send(JSON.stringify(textQueryPayload));
    startResponseTimeout();
    setUserText('');
    setDocumentIds([]);
    setTextQueryUserEmotion(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSession, userText, documentIds, isWsConnected, textQueryUserEmotion]);

  useEffect(() => {
    if (!hasSession) return;
    if (!userInteract || !isWsConnected || !wsRef.current) return;

    wsRef.current.send(JSON.stringify(userInteract));
    setUserInteract(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSession, userInteract, isWsConnected]);

  useEffect(() => {
    if (shouldRouteLocalRef.current) {
      isLocalAudioRouteRef.current = true;
      return;
    }
    if (!wsRef.current) return;
    isLocalAudioRouteRef.current = false;
  }, [isAvatarHidden, isVideoStreamReady]);

  useEffect(() => {
    if (
      isWsConnected &&
      !isClientInitialized &&
      (isVideoStreamReady || isAvatarHidden)
    ) {
      setIsClientInitialized(true);
      if (!isIOS()) {
        postMessage({ type: MESSAGE_TYPES.INITIALIZED });
      }
    }
  }, [
    isAvatarHidden,
    isClientInitialized,
    isVideoStreamReady,
    isWsConnected,
    setIsClientInitialized
  ]);

  useEffect(() => {
    if (!isWsConnected || !wsRef.current || !isEmotionRecognitionEnabled) return;
    if (aiResponseInFlightRef.current || isAiTalkingGuardRef.current) return;
    if (isLiveChatActive) return;

    const userEmotionField = buildUserEmotionWsField(userEmotion);
    if (!userEmotionField) return;

    const emotionPayload = {
      type: NUDGYT_AGENT_MESSAGE_TYPES.TEXT_QUERY,
      ...userEmotionField
    };

    wsRef.current.send(JSON.stringify(emotionPayload));

    postMessage({
      type: MESSAGE_TYPES.CHAT,
      payload: {
        from: MESSAGE_FROM.USER,
        type: NUDGYT_AGENT_MESSAGE_TYPES.USER_EMOTION,
        emotion: userEmotionField.user_emotion
      }
    });
  }, [isEmotionRecognitionEnabled, isLiveChatActive, isWsConnected, userEmotion]);
}

export default useAnamNudgytAgentCoreClient;
